import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "emotion-theming";
import { getPlatform } from "../../../helpers";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { CommonContextContext } from "wrapper/CommonWrapper/context";
import { PAYMENT_MODES } from "constants/paymentModes";
import { getPaymentAlert } from "../../../logics/src/utils/getPaymentAlert";
import { PAYMENT_METHODS } from "constants/payment";
import { getValidateUPI, validateBankOffer } from "store/payment/services";
import { IOfferMessage, OfferDataInterface } from "wrapper/PaymentWrapper/dependencies/utils/@types/savedMethods";
import { getStringFromId } from "helpers/vernacular";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { validateVPAField } from "helpers/utils/validateVpaFiels";
import {
  getTransformedVpaList,
  getVpaDetails,
  getVpaSuffixList,
  validateInputOnVpaChange,
} from "../common/dependencies/dependencyhelpers";
import { getOfferKeyList, getOfferMessages } from "../../../logics/src/utils/helpers";
import { doCheckOffers } from "../../../logics/src/utils/checkOffers";
import {
  getSeparatedUpiData,
  getUpiInfoDetails,
  validateVpaHandle,
} from "./helpers";
import { ValidateBankOfferParams } from "store/payment/@types";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { PAYMENT_ERROR_MESSAGE } from "constants/paymentStatus";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";
import { getHexToRgba } from "@nykaa/payment-ui/src/common/helpers/getHexToRgba";
import { ErrorModalContext } from "wrapper/ErrorModalWrapper/context";
import { doAttachWebCallback, WebCallback } from "helpers/utils/webView";
import { DEVICE_TYPE, BUTTON_TYPE } from "constants/index";

import UpiDesktop from "@nykaa/payment-sdk/src/UpiIntent/desktop";
import UPI from "@nykaa/payment-sdk/src/UpiIntent/mobile";
import { logger } from "logging";
import { useHistory } from "react-router-dom";
import { UPI_ERROR_MESSAGE } from "layouts/Payment/common/dependencies/constants";
import { getUpiIntentListFromRemoteConfig, checkAllUpiIntentVisibilityOnWeb } from "layouts/CheckoutPayment/helpers";
import { INTENT_NO_APPS_ERROR, NO_APPS_ERROR_MESSAGE } from "wrapper/PaymentWrapper/dependencies/constants";
import { isIosWeb } from "helpers/deviceType";
import { UpiIntentPollingScreen } from "layouts/CheckoutPayment/UpiIntentPollingScreen";
import { formatNumber } from "helpers/utils/numberFormatter";
import { isOnlyQrAvailable } from "helpers/utils";
import { trackOtherUpiClick } from "../common/dependencies/tracking";


const UpiWrapper = ({
  cartRules,
  doPayment,
  isAccordianOpened,
  toggleNewPaymentAccordian,
  endIcon,
  timerCallback,
  handleScanAndPayClick,
  setUpiDetails,
  isDisabled,
  isGPayIntentAvailable,
  strikedPaymentText,
  toggleOfferBottomSheet,
  qrOffers,
  hideHeader = false,
  isButtonFixedToBottom = false,
  textIcon = "",
  isOnlyUpiIntentAvailable = false,
}: any) => {
  const INVALID_UPI_ID = "Invalid UPI Id";
  const OTHER_UPI = "Other UPI";
  const { UpiIntentMweb, UpiIntentMwebPollingScreen, QrCodeExp, VpaSuffixChips } = useContext(ExperimentAndSwitchContext);
  const paymentContext: any = useContext(PaymentContext);
  const { orderAmount, paymentInfo, cartInfo } = useContext(CommonContextContext);
  const { applicationData } = useContext(ApplicationContext);
  const errorModalContext = useContext(ErrorModalContext);
  const vpaSuffixtimer = useRef() as any;

  const openErrorModal = errorModalContext?.openErrorModal;
  const theme = useTheme() as Theme;
  const history = useHistory();
  const platform = getPlatform();
  const regexforExcludingSpecialChar = /[@]/;
  const initalVpaState = {
    error: "",
    hasError: false,
    showSavedVpa: false,
    title: "",
    vpa: "",
    icon: "",
    notifyIcon: "",
    message: "",
    isQrFlow: false,
  };

  const grandTotal = orderAmount;
  const setLoading = paymentContext?.setLoading;
  const setIsPaymentProcessing = paymentContext?.setIsPaymentProcessing;
  const isCartRulesLoading = paymentContext?.isCartRulesLoading;
  const upiMapping = paymentInfo?.upiMapping;
  const paymentAlertInfo = paymentInfo?.paymentAlertInfo;
  const deviceType = applicationData?.deviceType;
  const isApp = applicationData?.isApp;
  const upiIntentList = paymentContext?.upiIntentList;
  const initialOrderAmount = cartInfo?.grandTotal; // this is the order amout which we get in cart refresh call
  const availablePaymentMethods = cartInfo?.availablePaymentMethods;
  const cashFreePaymentGateway = cartInfo?.paymentGateway;
  const upiIntentQR = paymentInfo?.upiIntentQR; // backend key to enable disable QR code payment method

  /**
   * Note: for desktop alert config is managed in useSideBar hook
   */
  const alertConfig = getPaymentAlert(paymentAlertInfo, PAYMENT_MODES.UPI);
  const alertMessage = alertConfig?.alertMessage;
  const isPaymentEnabled = alertConfig?.isPaymentEnabled;
  const isPaymentMethodVisible = alertConfig?.isPaymentMethodVisible;
  const isAllUpiIntentAvailableOnWeb = checkAllUpiIntentVisibilityOnWeb({
    availablePaymentMethods,
    isExperimentEnable: UpiIntentMweb.experimentEnabled && UpiIntentMweb.switchEnabled,
    cashFreePaymentGateway,
  });
  const isQrCodeAvailable = upiIntentQR && QrCodeExp.experimentEnabled && QrCodeExp.switchEnabled;

  /**
   * upi method name irrespective of upiMapping titles. it is for upi intent.
   */
  const UPI_METHODS: any = {
    "Google Pay": PAYMENT_METHODS.GOOGLE_PAY,
    PhonePe: PAYMENT_METHODS.PHONE_PE,
    ICICI: PAYMENT_METHODS.ICICI,
    BHIM: PAYMENT_METHODS.BHIM,
    Paytm: PAYMENT_METHODS.PAYTM,
    "CRED Pay UPI": PAYMENT_METHODS.CRED_PAY_UPI,
  };

  /**
   * This is only for MWEB.
   */
  let availableUpiMethodList = [
    { title: "Google Pay", icon: getIcons(platform, ICONS_NAMES.GOOGLE_PAY), merchantKey: "gpay" },
    { title: "PhonePe", icon: getIcons(platform, ICONS_NAMES.PHONE_PE), merchantKey: "phonepe" },
    { title: "Paytm", icon: getIcons(platform, ICONS_NAMES.PAYTM), merchantKey: "paytm" },
  ];

  /**
     * upiIntent web exp is enable then fetching upi list form
     * remoteConfig.
     */
   if (isAllUpiIntentAvailableOnWeb) {
    availableUpiMethodList = getUpiIntentListFromRemoteConfig();
  }

  /**
   * using this for mweb.
   */
  /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
  const Intentpackages = {} as Iintentpackages;
  upiMapping.forEach((list: IupiMapping) => {
    if (UPI_METHODS[list.title]) {
      Intentpackages[UPI_METHODS[list.title]] = {
        packageName: list.package,
        offerKeys: list.offerKeys || null,
      };
    }
  });

  let defaultSelectedMethod = isQrCodeAvailable ? PAYMENT_METHODS.QR : PAYMENT_METHODS.GOOGLE_PAY;
  // it is for default selected method incase of upi intent.
  if (isApp && upiIntentList?.length) {
    const intentTitle = upiIntentList?.[0]?.title;
    defaultSelectedMethod = UPI_METHODS[intentTitle];
  }

  const [vpaDetails, setVpa] = useState(initalVpaState as IvpaDetails);
  const [selectedUpi, onSelectUpi] = useState(defaultSelectedMethod);
  const [selectedVpaHandle, onSelectVpaHandle] = useState("");
  const [vpaSuffixList, setVpaSuffixList] = useState([]);
  const [isBottomSheetOpened, setBottomSheet] = useState(false);
  const [isDropdownOpened, toggleDropDown] = useState(false);
  const [vpaAPIError, setVpaAPIError] = useState("");
  /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
  const [offerData, setOfferData] = useState({} as OfferDataInterface);
  const [selectedUpiMethod, setSelectedUpiMethod] = useState(defaultSelectedMethod);
  const [validatedPayerName, setValidatedPayerName] = useState("");
  const [disablePspList, setDisablePspList] = useState([] as string[]);
  const [errorText, setErrorText] = useState("");
  const [showPollingScreen, setShowPollingScreen] = useState(false);
  const [vpaSuffix, setVpaSuffix] = useState("");
  const [availableVpaList, setAvailableVpaList] = useState([]);
  let qrOfferMessages: IOfferMessage[] = [];

  /**
   * function for upi intent error handling mweb.
   */
  const upiIntentMwebErrorHandler = (merchantKey: string, error_id: string) => {
    if (error_id === INTENT_NO_APPS_ERROR) {
      setDisablePspList((list: string[]) => [...list, merchantKey]);
      /**
       * if intent app is not found then select the other upi option.
       * NOTE: Skip this in case collect is disabled
       */
      if (!isOnlyUpiIntentAvailable) {
        upiMethodToggleAccordian(PAYMENT_METHODS.UPI);
      }
      setErrorText(NO_APPS_ERROR_MESSAGE);
    }
    return;
  };

  /**
   * function for opening and closing upi intent polling screen on mweb.
   */
  const handleMwebIntentPollingScreen = (value: boolean) => {
    setShowPollingScreen(value);
  };

  /**
   * function for upi intent error handling.
   */
  const upiIntentErrorHandler = () => {
    setLoading(false);
    const errorMsg = "Please enter UPI ID manually.";
    const err = new Error(`Unable to launch PSP ${selectedUpi}`);
    logger.error(err);
    openErrorModal(PAYMENT_ERROR_MESSAGE.SOMETHING_WENT_WRONG, errorMsg);
    /**
     * if intent app is not found then select the other upi option.
     * NOTE: Skip this in case collect is disabled
     */
    if (!isOnlyUpiIntentAvailable) {
      setSelectedUpiMethod(PAYMENT_METHODS.UPI);
    }
  };

  useEffect(() => {
    // it is for webview.
    if (isApp) {
      doAttachWebCallback({
        callbackName: WebCallback.DO_OPEN_INTENT_ERROR_CALLBACK,
        callbackDeclaration: upiIntentErrorHandler,
      });
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  /**
   * if PSP apps not installed in user's device then select the other upi.
   */
  useEffect(() => {
    // it is for default selected method incase of upi intent.
    if (isApp) {
      let selectedMethod = PAYMENT_METHODS.UPI;
      if (upiIntentList?.length) {
        const intentTitle = upiIntentList?.[0]?.title;
        selectedMethod = UPI_METHODS[intentTitle];
      }
      setSelectedUpiMethod(selectedMethod);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [upiIntentList]);

  useEffect(() => {
    onSelectUpi(selectedUpiMethod);
  }, [selectedUpiMethod]);

  useEffect(() => {
    setSelectedUpiMethod(defaultSelectedMethod);
  }, [defaultSelectedMethod]);

  /**
   * when availableUpiMethodList is 0 then byDefault open
   * other upi.
   */
  useEffect(() => {
    if (!isApp && availableUpiMethodList.length === 0) {
      upiMethodToggleAccordian(PAYMENT_METHODS.UPI);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [availableUpiMethodList]);

  const upiInvalidMessage = getStringFromId(
    VERNACULAR_STRING_IDS.UPI_ADDRESS_NOT_VALID,
    VERNACULAR_DEFAULT_MESSAGES.UPI_ADDRESS_NOT_VALID
  );
  const upiErrorMessage = getStringFromId(
    VERNACULAR_STRING_IDS.SOMETHING_WENT_WRONG,
    VERNACULAR_DEFAULT_MESSAGES.SOMETHING_WENT_WRONG
  );
  const requiredErrorMessage = getStringFromId(
    VERNACULAR_STRING_IDS.THIS_FIELD_IS_REQUIRED,
    VERNACULAR_DEFAULT_MESSAGES.THIS_FIELD_IS_REQUIRED
  );
  const rateLimitError = getStringFromId(
    VERNACULAR_STRING_IDS.UPI_ADDRESS_MAXIMUM_LIMIT_REACH,
    VERNACULAR_DEFAULT_MESSAGES.UPI_ADDRESS_MAXIMUM_LIMIT_REACH
  );
  const upiCollectDisabledMessage = getStringFromId(
    VERNACULAR_STRING_IDS.UPI_COLLECT_DISABLED,
    VERNACULAR_DEFAULT_MESSAGES.UPI_COLLECT_DISABLED
  );
  /**
   *
   * @param vpa vpa id (eg. 9876543210@upi)
   * @returns errors object contains meessage and hasError boolean value
   */

  const validateInputs = async (vpa: string) => {
    const vpaVernacularError = {
      invalid: upiInvalidMessage,
      required: requiredErrorMessage,
    };

    const formStatus = validateVPAField(vpa, vpaVernacularError);
    const errors = {
      error: formStatus.error,
      hasError: formStatus.hasError,
    };
    return errors;
  };

  /**
   *
   * @param value vpa id (eg. 9876543210@upi)
   * @param isValidate optional parameter for doing the validation or not. default value is true.
   * @description validateVpaInputs does that same validation as before but separated out to reuse the logic.
   */

   const validateVpaInputs = async (value: string, isValidate = true) => {
    const vpaData = getVpaDetails(value, upiMapping);
    if (isValidate) {
      validateInputs(value)
      .then(response => {
        setVpa({
          ...vpaData,
          ...response,
        });
      })
      .catch(() => {
        setVpa({
          ...vpaData,
          error: upiErrorMessage || UPI_ERROR_MESSAGE.ERROR,
          hasError: true,
        });
        // logger.info(error, 'Upi handleInputChange');
      });
    } else {
      setVpa({ ...initalVpaState, ...vpaData });
    }
  };

  /* eslint-disable no-shadow */
  const handleSubmit = async ({ vpa, upiPaymentInfo, selectedUpi }: IvpaSubmitClick) => {
    const paymentMethodTracking = PAYMENT_METHODS.UPI;
    // Call validateVPA here
    let callCreateOrderApi = true;
    let payerAccountName;
    const isVpaAndOfferVerificationEnabled = !offerData?.hasOffers;

    if (isVpaAndOfferVerificationEnabled) {
      callCreateOrderApi = false;
      // clearing error(if exists) when user submits upi
      if (vpaAPIError) {
        setVpaAPIError?.("");
      }
      // hit the upi validation API
      let res;
      try {
        res = await getValidateUPI({ customerVpa: vpa });
        payerAccountName = res?.data?.payerAccountName;
        setValidatedPayerName(payerAccountName);
      } catch (error) {
        logger.error(error);
        res = {
          success: false,
          message: rateLimitError
        }
      }
      let offerRes;
      let offer = {} as OfferDataInterface;

      if (cartRules?.paymentRules && res?.success) {
        const offerKeys = getOfferKeyList(cartRules?.paymentRules, PAYMENT_MODES.UPI);
        if (offerKeys?.length) {
          try {
            const validateBankOfferParams: IvalidateBankOfferParams = {
              deviceType,
              mode: PAYMENT_MODES.UPI.toUpperCase(),
              offerKeyList: offerKeys,
              paymentInstrumentDTO: {
                customerVPA: vpa,
              },
            };

            offerRes = await validateBankOffer(validateBankOfferParams);
            offer = doCheckOffers(
              false,
              PAYMENT_MODES.UPI,
              cartRules,
              paymentInfo,
              offerRes?.eligibleOffers
            );
          } catch (e) {
            logger.error(e);
          }
        }
      }

      // when getting error "Invalid VPA or No account linked to this VPA", show it as inline error
      if (res && !res.success) {
        setVpaAPIError(res?.message || INVALID_UPI_ID);
        setLoading(false);
        setIsPaymentProcessing(false);
      } else if (offerRes?.eligibleOffers?.length && offer?.hasOffers) {
        setLoading(false);
        setIsPaymentProcessing(false);
        setOfferData(offer);
      } else {
        callCreateOrderApi = true;
      }
    }
    if (callCreateOrderApi) {
      if (offerData?.hasOffers) {
        vpaDetails.offerData = offerData;
      }
      setUpiDetails(vpaDetails);
      doPayment(PAYMENT_MODES.UPI, {
        vpa,
        upiPaymentInfo,
        paymentMethodTracking,
        isSavedUpi: false,
        selectedUpi,
        timerCallback,
        payerAccountName: validatedPayerName || payerAccountName,
        offerData,
        // Required for adding Money to wallet and giftcard purchase
        grandTotal,
        intentApp: selectedUpi,
      });
    }
  };

  const toggleBottomSheet = () => {
    setBottomSheet(!isBottomSheetOpened);
  };

  const validateVpaInput = (vpa: string) => {
    try {
      return validateInputOnVpaChange(selectedUpi, vpa);
    } catch (error) {
      // logger.error(error, 'Error in validateVpaInput');
    }
    return vpa;
  };

  /**
   *
   * @param value selected vpaSuffix from dropdown
   * @description handleVpaHandleSelection handles for both provided UPI lists and manually entered upi.
   */

  const handleVpaHandleSelection = (value: any) => {
    // clear the  vpaAPIError message when user, selects  different upi through dropdown
    if (vpaAPIError) {
      setVpaAPIError?.("");
    }
    try {
      const newValue: string = value?.text || value;
      if (vpaDetails.vpa && vpaDetails.vpa.includes("@")) {
        let separatedUpiData = getSeparatedUpiData(vpaDetails.vpa);
        if (newValue != OTHER_UPI) {
          validateVpaInputs(`${separatedUpiData.vpaId}${newValue}`);
        }
      } else if (vpaDetails.vpa) {
        validateVpaInputs(`${vpaDetails.vpa}${newValue}`);
      }

      if (newValue == OTHER_UPI) {
        setSelectedUpiMethod(PAYMENT_METHODS.UPI);
        onSelectUpi(PAYMENT_METHODS.UPI);
      } else {
        onSelectVpaHandle(newValue);
      }
      toggleDropDown(!isDropdownOpened);
    } catch (error) {
      // logger.error(error, 'Error in handleVpaHandleSelection');
    }
  };

  const handleInputChange = (value: string, specialCharAllowed: boolean = false, isVpaFilterationAllowed: boolean = true) => {
    const vpaWithoutSuffix = value.split("@");

    /**
     * using debouncing to filter out vpa suffix;
     */
    clearTimeout(vpaSuffixtimer.current);
    vpaSuffixtimer.current = setTimeout(() => {
      if (isVpaFilterationAllowed) {
        /**
         * filterig vpa suffix based on input value.
         */
        const filteredVpaList = (paymentInfo?.vpaSuffixList || []).filter((suffix: string) =>
          vpaWithoutSuffix[1] ? suffix.match(vpaWithoutSuffix[1]) : suffix
        );

        setAvailableVpaList(filteredVpaList);
      }

      /**
       * showing vpa suffix chip selected when
       * vpaWithoutSuffix array length is greater
       * then 2.
       */
      if (vpaWithoutSuffix.length > 1) {
        setVpaSuffix(`@${vpaWithoutSuffix[1]}`);
      } else {
        setVpaSuffix("");
      }
    }, 500);

    /**
     * specialChar is allowed only in other upi method
     */
    if (!specialCharAllowed && value.match(regexforExcludingSpecialChar)) {
      return;
    }

    /**
     *  clear the  vpaAPIError message when user, changes upi id
    */
    if (vpaAPIError) {
      setVpaAPIError?.("");
    }
    if (offerData?.hasOffers) {
      setOfferData({} as OfferDataInterface);
    }
    /**
     * condition added for upi validation and to reuse existing code
     * in case of selected upi other than bhim upi
     */
    if (
      value &&
      selectedUpi != PAYMENT_METHODS.UPI &&
      selectedVpaHandle &&
      selectedVpaHandle != OTHER_UPI
    ) {
      value = `${value}${selectedVpaHandle}`;
    }

    validateVpaInputs(value, false);
  };

  useMemo(async () => {
    if (!isCartRulesLoading && vpaDetails?.vpa) {
      if (cartRules?.paymentRules) {
        const offerKeys = getOfferKeyList(cartRules?.paymentRules, PAYMENT_MODES.UPI);
        if (offerKeys?.length) {
          const validateBankOfferParams: ValidateBankOfferParams = {
            deviceType,
            mode: PAYMENT_MODES.UPI.toUpperCase(),
            offerKeyList: offerKeys,
            paymentInstrumentDTO: {
              customerVPA: vpaDetails.vpa,
            },
          };
          try {
            const offerRes = await validateBankOffer(validateBankOfferParams);
            const offer: OfferDataInterface = doCheckOffers(
              false,
              PAYMENT_MODES.UPI,
              cartRules,
              paymentInfo,
              offerRes?.eligibleOffers
            );
            setOfferData(offer);
          } catch (e) {
            logger.error(e);
          }
        }
      }
    }
  }, [isCartRulesLoading]);

  useMemo(() => {
    // setting the validateVPA api error to inline errors
    if (vpaAPIError) {
      setVpa({ ...vpaDetails, hasError: true, error: vpaAPIError });
    }
  }, [vpaAPIError]);
  // this effect method is to fetch vpa suffix List of select upi

  useMemo(() => {
    const vpaList = getVpaSuffixList(selectedUpi);
    if (vpaList && vpaList.length) {
      if (vpaDetails.vpa && vpaDetails.vpa.includes("@")) {
        let separatedUpiData = getSeparatedUpiData(vpaDetails.vpa);
        validateVpaInputs(`${separatedUpiData.vpaId}${separatedUpiData.vpaSuffix}`);
        onSelectVpaHandle(separatedUpiData.vpaSuffix);
      } else if (vpaDetails.vpa) {
        validateVpaInputs(`${vpaDetails.vpa}${vpaList[0]}`);
      } else {
        onSelectVpaHandle(vpaList[0]);
      }
      setVpaSuffixList(vpaList);
    } else {
      onSelectVpaHandle("");
    }
    toggleDropDown(false);
    if (offerData?.hasOffers) {
      setOfferData({} as OfferDataInterface);
    }
  }, [vpaDetails.vpa]);

  useEffect(() => {
    const vpaList = getVpaSuffixList(selectedUpi);
    if (vpaList && vpaList.length) {
      if (vpaDetails.vpa && vpaDetails.vpa.includes("@")) {
        let separatedUpiData = getSeparatedUpiData(vpaDetails.vpa);
        validateVpaInputs(`${separatedUpiData.vpaId}${vpaList[0]}`);
        onSelectVpaHandle(vpaList[0]);
      } else if (vpaDetails.vpa) {
        validateVpaInputs(`${vpaDetails.vpa}${vpaList[0]}`);
      } else {
        onSelectVpaHandle(vpaList[0]);
      }
      setVpaSuffixList(vpaList);
    }
  }, [selectedUpi]);

  useEffect(() => {
    setOfferData({} as OfferDataInterface);
  }, [grandTotal]);

  const handleUPIClick = (intentOffers?: any, currentMethod? : string, merchantKey = "") => {
    // Using isPaymentProcessing state to avoid multiple trigger of tracking evnets when loading state changes
    setIsPaymentProcessing(true);
    setLoading(true);

    /**
     * This is for upiIntent exp on mweb.
     */
    if (
      (isGPayIntentAvailable && currentMethod === PAYMENT_METHODS.GOOGLE_PAY) ||
      (isAllUpiIntentAvailableOnWeb && currentMethod !== PAYMENT_METHODS.UPI) || isOnlyUpiIntentAvailable
    ) {
      doPayment(PAYMENT_MODES.INTENT, {
        isSavedUpi: false,
        timerCallback,
        offerData: intentOffers || {},
        grandTotal,
        packageName: Intentpackages[currentMethod || ""]?.packageName,
        intentApp: selectedUpi,
        merchantKey,
        errorCallback: upiIntentMwebErrorHandler,
        handleMwebIntentPollingScreen,
      });

      /**
       * for ios after selecting the UPI app from list of apps then
       * select the other upi after 1s.
       */
      if (isIosWeb() && !isOnlyUpiIntentAvailable) {
        setTimeout(() => {
          upiMethodToggleAccordian(PAYMENT_METHODS.UPI);
        }, 1000);
      }

      return;
    }
    let { vpa } = vpaDetails;

    if (selectedUpi != PAYMENT_METHODS.UPI && selectedVpaHandle && selectedVpaHandle != OTHER_UPI) {
      vpa = validateVpaHandle(selectedUpi, vpa);
      vpa = `${vpa}${selectedVpaHandle}`;
    }
    validateInputs(vpa).then(response => {
      const { hasError, error } = response;
      if (hasError) {
        setVpa({
          ...vpaDetails,
          hasError,
          error,
        });
        setLoading(false);
        setIsPaymentProcessing(false);
      } else {
        setVpa({
          ...vpaDetails,
          hasError,
          error,
        });
        vpaDetails.amountToPay = grandTotal;
        setUpiDetails(vpaDetails);

        if (selectedUpi != PAYMENT_METHODS.UPI && selectedVpaHandle) {
          handleSubmit({ vpa, selectedUpi });
        } else {
          handleSubmit({ vpa });
        }
      }
    });
  };

  const upiInfoDetails = getUpiInfoDetails(selectedUpi);

  let title = getStringFromId(VERNACULAR_STRING_IDS.UPI, VERNACULAR_DEFAULT_MESSAGES.UPI);
  if (deviceType === DEVICE_TYPE.DESKTOP) {
    title = getStringFromId(
      VERNACULAR_STRING_IDS.UPI_DESKTOP,
      VERNACULAR_DEFAULT_MESSAGES.UPI_DESKTOP
    );
  }
  const subTitle = getStringFromId(
    VERNACULAR_STRING_IDS.UPI_SUBTITLE,
    VERNACULAR_DEFAULT_MESSAGES.UPI_SUBTITLE
  );
  const labelText = getStringFromId(
    VERNACULAR_STRING_IDS.UPI_INPUT_LABEL,
    VERNACULAR_DEFAULT_MESSAGES.UPI_INPUT_LABEL
  );
  const gotIt = getStringFromId(VERNACULAR_STRING_IDS.GOT_IT, VERNACULAR_DEFAULT_MESSAGES.GOT_IT);

  const payNowString = getStringFromId(VERNACULAR_STRING_IDS.DYNAMIC_PAY, `Pay ₹${formatNumber(grandTotal)}`, {
    count: grandTotal,
  });
  const payNowStringAndVerify = getStringFromId(
    VERNACULAR_STRING_IDS.DYNAMIC_PAY_WITH_VERIFY,
    `Verify & Pay ₹${formatNumber(grandTotal)}`,
    { count: grandTotal }
  );

  const scanAndPay = getStringFromId(
    VERNACULAR_STRING_IDS.DYNAMIC_SCAN_AND_PAY,
    `Scan & Pay ₹${formatNumber(grandTotal)}`,
    { count: grandTotal }
  );

  const getPayNowString = (enableCollect: boolean) => {
    // show verify and pay text once user start entering upi id
    if (enableCollect && vpaDetails?.vpa) {
      return payNowStringAndVerify;
    }
    return payNowString;
  };

  const getRevisedPayNowString = ({
    intentOffer,
    enableCollect,
    isQrFlow,
  }: {
    intentOffer?: any;
    enableCollect?: boolean;
    isQrFlow?: boolean
  }) => {
    if (isQrFlow && qrOffers?.hasOffers) {
      return getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_SCAN_AND_PAY,
        `Scan & Pay ₹${formatNumber(qrOffers?.finalOrderAmount)} &nbsp; <del> ₹${formatNumber(initialOrderAmount)}</del>`,
        { oldAmount: initialOrderAmount, newAmount: qrOffers?.finalOrderAmount || "" }
      );
    }
    if (intentOffer?.hasOffers) {
      return getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_PAY_WITH_STRIKED_AMOUNT,
        `Pay ₹${formatNumber(intentOffer?.finalOrderAmount)} &nbsp; <del> ₹${formatNumber(initialOrderAmount)}</del>`,
        { oldAmount: initialOrderAmount, newAmount: intentOffer?.finalOrderAmount || "" }
      );
    } else if (offerData?.hasOffers) {
      return getStringFromId(
        VERNACULAR_STRING_IDS.DYNAMIC_PAY_WITH_STRIKED_AMOUNT,
        `Pay ₹${formatNumber(offerData?.finalOrderAmount)} &nbsp; <del> ₹${formatNumber(initialOrderAmount)}</del>`,
        { oldAmount: initialOrderAmount, newAmount: offerData?.finalOrderAmount || "" }
      );
    } else if (enableCollect && vpaDetails?.vpa) {
      return payNowStringAndVerify;
    }
    return strikedPaymentText;
  };

  const otherUpiText = getStringFromId(
    VERNACULAR_STRING_IDS.OTHER_UPI,
    VERNACULAR_DEFAULT_MESSAGES.OTHER_UPI
  );

  const qrTitle = getStringFromId(
    VERNACULAR_STRING_IDS.QR_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.QR_TITLE
  );

  const qrSubTitle = getStringFromId(
    VERNACULAR_STRING_IDS.QR_SUB_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.QR_SUB_TITLE
  );

  /**
   * it is for upi intent submission. it initiates the order.
   */
  const handleIntentSubmit = (intentConfig: any, intentOffers: any) => {
    setLoading(true);
    const packageName = intentConfig?.package;
    const intentApp = UPI_METHODS[intentConfig?.title];

    /**
     * this is for title for polling screen in IOS
     */
    vpaDetails.amountToPay = grandTotal;
    setUpiDetails(vpaDetails);
    doPayment(PAYMENT_MODES.INTENT, {
      packageName,
      isSavedUpi: false,
      timerCallback,
      offerData: intentOffers,
      grandTotal,
      intentApp,
    });
  };

  const upiMethodToggleAccordian = (method: string, merchantKey = "") => {
    if (selectedUpiMethod !== method) {
      setVpa(initalVpaState);
    }

    /**
     * For showing vpa suffix chips
     * When user click in other upi
     */
    if ( VpaSuffixChips.experimentEnabled && method === PAYMENT_METHODS.UPI ) {
      setAvailableVpaList(paymentInfo?.vpaSuffixList);
      setVpaSuffix("");
    }
    if (method === PAYMENT_METHODS.UPI && isOnlyUpiIntentAvailable) {
      trackOtherUpiClick({
        deviceType: deviceType,
        orderAmount: grandTotal,
      })
    }

    /**
     * handling for disable state of psp.
     */
    if (disablePspList.includes(merchantKey)) return;

    /**
     * clearing errorText when user selects diffrent PSP app.
     */
    setErrorText("");
    setSelectedUpiMethod(method);
  };

  const getEndIconForUpiMethod = (isAccordianOpened: boolean) => {
    const platform = getPlatform();
    return isAccordianOpened
      ? getIcons(platform, ICONS_NAMES.RADIO_ACTIVE)
      : getIcons(platform, ICONS_NAMES.RADIO);
  };

  /**
   * function for check the upi collect flow is enable or not.
   * incase of webview, gpayIntent and intent exp for MSITE, make collect as false.
   */
  const checkEnableCollect = (method = " ") => {
    let enableCollect = true;
    if (
      isApp ||
      (method === PAYMENT_METHODS.GOOGLE_PAY && isGPayIntentAvailable) ||
      isAllUpiIntentAvailableOnWeb ||
      isOnlyUpiIntentAvailable
    ) {
      enableCollect = false;
    }
    return enableCollect;
  };

  const getChip = (type?: string) => {
    let chipConfig = {};
    if (type === "success") {
      chipConfig = {
        text: theme?.colors?.positive,
        border: getHexToRgba(theme?.colors?.positive, 0.16),
        background: theme?.colors?.positive50,
      };
    } else {
      chipConfig = {
        text: theme?.colors?.info,
        border: getHexToRgba(theme?.colors?.info, 0.16),
        background: theme?.colors?.info50,
      };
    }
    return chipConfig;
  };

  /**
   * function appends the suffix to vpa.
   */
  const doAppendVpaSuffix = (value: string) => {
    setVpaSuffix(value);
    const currentVpa = vpaDetails.vpa;
    const vpaWithoutSuffix = currentVpa.split("@");
    /**
     * removing existing vpaSuffix and updating with new one.
     */
    const updatedVpa = vpaWithoutSuffix[0] + value;
    handleInputChange(updatedVpa, true, false);
  };

  // for upi method config
  let upiMethodList = [];

  qrOfferMessages = getOfferMessages(qrOffers, theme, () => {
    toggleOfferBottomSheet(qrOffers);
  });

  const qrCodeConfig = {
    title: qrTitle,
    titleFont: theme.typography.titleSmall,
    titleColor: getHexToRgba(theme?.colors?.textPrimary, 0.92),
    subTitle: selectedUpiMethod === PAYMENT_METHODS.QR ? "" : qrSubTitle,
    subTitleFont: theme.typography.subTitleMedium,
    subTitleColor: getHexToRgba(theme?.colors?.textPrimary, 0.92),
    isAccordianOpened: selectedUpiMethod === PAYMENT_METHODS.QR,
    payNowText: scanAndPay,
    revisedPayNowText: getRevisedPayNowString({ enableCollect: true, isQrFlow: true }),
    endIcon: getEndIconForUpiMethod(selectedUpiMethod === PAYMENT_METHODS.QR),
    isSubmitButtonDisabled: false,
    toggleAccordian: () => upiMethodToggleAccordian(PAYMENT_METHODS.QR),
    doPayment: () => handleScanAndPayClick(),
    theme,
    id: PAYMENT_METHODS.UPI,
    errorText: qrOffers?.hasOffers ? qrOffers?.discountAmountMsg : vpaDetails.error,
    isBankOfferApplied: qrOffers?.hasOffers,
    dataTestId: "qrCode",
    buttonType: BUTTON_TYPE.MEDIUM,
    radioIcon: getIcons(platform, ICONS_NAMES.UPI_ACTIVE),
    qrCodeDefaultImage: getIcons(platform, ICONS_NAMES.DEFAULT_QR),
    upiAppIcons: [
      { icon: getIcons(platform as string, ICONS_NAMES.GOOGLE_PAY), alt: "Google Pay" },
      { icon: getIcons(platform as string, ICONS_NAMES.PHONE_PE), alt: "PhonePe" },
      { icon: getIcons(platform as string, ICONS_NAMES.PAYTM), alt: "Paytm" },
      { icon: getIcons(platform as string, ICONS_NAMES.CRED_PAY), alt: "Cred Pay" },
      { icon: getIcons(platform as string, ICONS_NAMES.UPI_PAYMENTS), alt: "UPI Payments" },
    ],
    qrOfferMessages,
  };

  /**
   * it is a common config for other upi,
   * incase of apps(android & ios), web(msite & desktop).
   */
  const otherUPIConfig = {
    title: otherUpiText,
    titleColor: getHexToRgba(theme?.colors?.pebble900, 0.92),
    isAccordianOpened: selectedUpiMethod === PAYMENT_METHODS.UPI,
    payNowText: payNowStringAndVerify,
    revisedPayNowText: getRevisedPayNowString({ enableCollect: true }),
    leadingIcon: getIcons(platform, ICONS_NAMES.OTHER_UPI),
    endIcon: getEndIconForUpiMethod(selectedUpiMethod === PAYMENT_METHODS.UPI),
    isSubmitButtonDisabled: isOnlyUpiIntentAvailable,
    toggleAccordian: () => upiMethodToggleAccordian(PAYMENT_METHODS.UPI),
    doPayment: () => handleUPIClick({}, PAYMENT_METHODS.UPI),
    theme,
    showCollect: !isOnlyUpiIntentAvailable,
    id: PAYMENT_METHODS.UPI,
    labelText,
    selectedVpa: selectedVpaHandle,
    isDropdownVisible: false,
    isDropdownOpened,
    vpaSuffixList: getTransformedVpaList(vpaSuffixList),
    vpa: vpaDetails.vpa,
    errorText: offerData?.hasOffers ? offerData?.discountAmountMsg : vpaDetails.error,
    offerText:
      selectedUpiMethod === PAYMENT_METHODS.UPI &&
      (offerData?.hasOffers ? offerData?.genericDiscountMsg : ""),
    chipColor: getChip(
      offerData?.hasOffers && selectedUpiMethod === PAYMENT_METHODS.UPI ? "success" : ""
    ),
    onBlur: validateInputs,
    onChange: (value: string) => handleInputChange(value, true),
    dropdownIcon: isDropdownOpened
      ? getIcons(platform, ICONS_NAMES.DROPDOWN_DOWN)
      : getIcons(platform, ICONS_NAMES.DROPDOWN_DOWN),
    toggleDropDown: () => {
      if (isPaymentEnabled) {
        toggleDropDown(!isDropdownOpened);
      }
    },
    onVpaSelection: handleVpaHandleSelection,
    validateVpaInput: validateVpaInput,
    inputIcon: getIcons(platform, ICONS_NAMES.INFO_ICON),
    isInputIconVisible: true, // info icon visibilitiy
    onInputIconClick: toggleBottomSheet,
    isBankOfferApplied: offerData?.hasOffers,
    dataTestId: otherUpiText,
    buttonType: BUTTON_TYPE.MEDIUM,
    radioIcon: getIcons(platform, ICONS_NAMES.UPI_ACTIVE),
    placeHolderText: getStringFromId(
      VERNACULAR_STRING_IDS.UPI_PLACEHOLDER,
      VERNACULAR_DEFAULT_MESSAGES.UPI_PLACEHOLDER
    ),
    availableVpaList,
    vpaSuffix,
    isOnlyUpiIntentAvailable: isOnlyUpiIntentAvailable,
    upiCollectDisabledMessage: isOnlyUpiIntentAvailable ? upiCollectDisabledMessage : "",
  };

  /**
   * it is for webview upi intent config.
   * showing the installed PSP apps upi intent.
   */
  if (isApp && upiIntentList?.length) {
    upiMethodList = upiIntentList?.map((intentConfig: any) => {
      const intentTitle = intentConfig?.title;
      const isAccordianOpened = selectedUpiMethod === UPI_METHODS[intentTitle];
      const intentOffers: any = doCheckOffers(
        false,
        PAYMENT_MODES.UPI,
        cartRules,
        paymentInfo,
        intentConfig?.offerKeys
      );
      const chipConfig = getChip(intentOffers?.hasOffers && isAccordianOpened ? "success" : "");

      return {
        title: intentTitle,
        titleColor: getHexToRgba(theme?.colors?.pebble900, 0.92),
        isAccordianOpened,
        payNowText: getPayNowString(false),
        revisedPayNowText: getRevisedPayNowString({
          intentOffer: intentOffers,
          enableCollect: false,
        }),
        leadingIcon: intentConfig?.iconIntentUrl,
        endIcon: getEndIconForUpiMethod(isAccordianOpened),
        isSubmitButtonDisabled: false,
        toggleAccordian: () => upiMethodToggleAccordian(UPI_METHODS[intentTitle]),
        doPayment: () => handleIntentSubmit(intentConfig, intentOffers),
        theme,
        showCollect: false,
        id: UPI_METHODS[intentTitle],
        labelText,
        selectedVpa: selectedVpaHandle,
        isDropdownVisible: selectedUpiMethod != PAYMENT_METHODS.UPI,
        isDropdownOpened,
        vpaSuffixList: getTransformedVpaList(vpaSuffixList),
        vpa: vpaDetails.vpa,
        errorText: vpaDetails.error,
        offerText: intentOffers?.hasOffers ? intentOffers?.genericDiscountMsg : "",
        chipColor: chipConfig,
        onBlur: validateInputs,
        onChange: handleInputChange,
        dropdownIcon: isDropdownOpened
          ? getIcons(platform, ICONS_NAMES.DROPDOWN_DOWN)
          : getIcons(platform, ICONS_NAMES.DROPDOWN_DOWN),
        toggleDropDown: () => {
          if (isPaymentEnabled) {
            toggleDropDown(!isDropdownOpened);
          }
        },
        onVpaSelection: handleVpaHandleSelection,
        validateVpaInput: validateVpaInput,
        inputIcon: getIcons(platform, ICONS_NAMES.INFO_ICON),
        isInputIconVisible: true, // info icon visibilitiy
        onInputIconClick: toggleBottomSheet,
        isBankOfferApplied: intentOffers?.hasOffers,
        buttonType: BUTTON_TYPE.MEDIUM,
        dataTestId: UPI_METHODS[intentTitle],
      };
    });
  }

  /**
   * it is for msite and desktop upi config.
   */
  if (!isApp) {
    upiMethodList = availableUpiMethodList.map(list => {
      const currentMethod = UPI_METHODS[list.title];
      const isAccordianOpened = selectedUpiMethod === currentMethod;
      const showCollect = checkEnableCollect(currentMethod);
      const merchantKey = list.merchantKey;

      let intentOffers: any = {};
      let offerMessage: any = [];
      /**
       * offers handling for upiIntent mweb.
       */
      if ((currentMethod == PAYMENT_METHODS.GOOGLE_PAY) || isAllUpiIntentAvailableOnWeb) {
        intentOffers = doCheckOffers(
          false,
          PAYMENT_MODES.UPI,
          cartRules,
          paymentInfo,
          Intentpackages[currentMethod || ""]?.offerKeys || []
        );

        /**
         * showing offer message in strip when new upi intent exp enable.
         */
        if (isAllUpiIntentAvailableOnWeb) {
          offerMessage = getOfferMessages(intentOffers, theme, () => {
            toggleOfferBottomSheet(intentOffers);
          });
        }
      }
      /**
       * For gpay intent for razorpay we are setting the intentOffers.
       * Now we are using offers instead of offerData becuase gpay intent has a different offers.
       */
      const offers = !showCollect ? intentOffers : offerData;
      const chipConfig = getChip(offers?.hasOffers && isAccordianOpened ? "success" : "");


      return {
        title: list.title,
        titleColor: getHexToRgba(theme?.colors?.pebble900, 0.92),
        isAccordianOpened,
        payNowText: getPayNowString(showCollect),
        revisedPayNowText: getRevisedPayNowString({ intentOffer: offers, enableCollect: showCollect }), // it is for stricked amount cta.
        leadingIcon: list.icon,
        endIcon: getEndIconForUpiMethod(isAccordianOpened),
        isSubmitButtonDisabled: false,
        toggleAccordian: () => upiMethodToggleAccordian(currentMethod, merchantKey),
        doPayment: () => handleUPIClick(intentOffers, currentMethod, merchantKey),
        theme,
        showCollect: showCollect,
        id: currentMethod,
        labelText,
        selectedVpa: selectedVpaHandle,
        isDropdownVisible: selectedUpiMethod != PAYMENT_METHODS.UPI,
        isDropdownOpened,
        vpaSuffixList: getTransformedVpaList(vpaSuffixList),
        vpa: vpaDetails.vpa,
        // using offers instead of offerData becuase gpay intent has a different offers.
        errorText: offers?.hasOffers ? offers?.discountAmountMsg : vpaDetails.error,
        offerText: (!showCollect || isAccordianOpened) && (offers?.hasOffers ? offers?.genericDiscountMsg : ""),
        chipColor: chipConfig,
        onBlur: validateInputs,
        onChange: handleInputChange,
        dropdownIcon: isDropdownOpened
          ? getIcons(platform, ICONS_NAMES.DROPDOWN_DOWN)
          : getIcons(platform, ICONS_NAMES.DROPDOWN_DOWN),
        toggleDropDown: () => {
          if (isPaymentEnabled) {
            toggleDropDown(!isDropdownOpened);
          }
        },
        onVpaSelection: handleVpaHandleSelection,
        validateVpaInput: validateVpaInput,
        inputIcon: getIcons(platform, ICONS_NAMES.INFO_ICON),
        isInputIconVisible: true, // info icon visibilitiy
        onInputIconClick: toggleBottomSheet,
        isBankOfferApplied: offerData?.hasOffers,
        dataTestId: list.title,
        buttonType: BUTTON_TYPE.MEDIUM,
        radioIcon: getIcons(platform, ICONS_NAMES.UPI_ACTIVE),
        offerMessage,
        isPSPdisable: disablePspList.includes(merchantKey),
      };
    });
  }

  /**
   * filter the upi method list based on the payment method visibility from remote config.
   * if vpaSuffixList in remote config for any psp i.e phonepe, gpay, paytm is empty/null then we are not showing the upi method.
   */
  if (!isApp && !isOnlyUpiIntentAvailable) {
    upiMethodList = upiMethodList.filter((method: any) => {
      return getVpaSuffixList(method.id)?.length > 0;
    });
  }

  upiMethodList.push(otherUPIConfig);

  const config = {
    isAccordianOpened,
    toggleAccordian: () =>
      toggleNewPaymentAccordian(isOnlyUpiIntentAvailable ? PAYMENT_MODES.INTENT : PAYMENT_MODES.UPI, isDisabled || !isPaymentEnabled),
    endIcon,
    title,
    subTitle,
    alert: {
      text: errorText ? errorText : alertMessage, //alertText,
      warn: true,
      font: theme?.typography?.paymentMethodAlert,
      leadingIcon: getIcons(platform, ICONS_NAMES.ALERT),
    },
    leadingIcon: getIcons(platform, ICONS_NAMES.UPI),
    isDisabled: isDisabled || !isPaymentEnabled,
    isHidden: !isPaymentMethodVisible,
    bottomSheetVisible: isBottomSheetOpened,
    bottomSheetTitle: upiInfoDetails.title,
    bottomSheetIcon: getIcons(platform, ICONS_NAMES.CLEAR),
    onBottomSheetClose: toggleBottomSheet,
    bottomSheetMaxHeight: 85,
    bottomSheetActionButtonText: gotIt,
    onBottomSheetActionButtonClick: toggleBottomSheet,
    upiInfo: upiInfoDetails,
    onBottomSheetIconClick: toggleBottomSheet,
    buttonType: BUTTON_TYPE.MEDIUM,
    isQrCodeAvailable,
    qrCodeConfig,
    upiMethodConfig: upiMethodList,
    isNewUpiDesignExpEnable: isAllUpiIntentAvailableOnWeb,
    doAppendVpaSuffix,
    hideHeader,
    isButtonFixedToBottom,
    history,
    theme,
    textIcon,
    titleFont: deviceType === DEVICE_TYPE.DESKTOP ? theme.typography.sectionTitle : theme.typography.subTitleLG,
  };

  /**
   * If UPI collect is not present in availableUpiMethodList and UPI intent is present,
   * we have to hide collect flow and show only intent(In this case QR code is powered by intent flow).
   */
  if (isOnlyQrAvailable(availablePaymentMethods, isQrCodeAvailable) && deviceType === DEVICE_TYPE.DESKTOP) {
    config.upiMethodConfig = [];
  }

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    return <UpiDesktop {...config} />;
  }

  return (
    <>
      <UPI {...config} theme={theme} />
      {UpiIntentMwebPollingScreen.experimentEnabled && (
        <UpiIntentPollingScreen
          theme={theme}
          showPollingScreen={showPollingScreen}
          handleMwebIntentPollingScreen={handleMwebIntentPollingScreen}
          history={history}
        />
      )}
    </>
  );
};

export default UpiWrapper;
